import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
	AlertTriangle,
	CheckCircle,
	Copy,
	ExternalLink,
	Eye,
	MapPin,
	RefreshCw,
	Settings,
	X,
	XCircle,
} from 'lucide-react';
import { ClientDetailsModalProps, CopyButtonProps, SetupInstructions } from '../../types';
import { adminAPI } from '../../services/api/adminAPI';

export const ClientDetailsModal: React.FC<ClientDetailsModalProps> = ({ isOpen, onClose, client }) => {
	// State for tabs and setup instructions
	const [activeTab, setActiveTab] = useState('details');
	const [setupInstructions, setSetupInstructions] = useState<SetupInstructions | null>(null);
	const [loadingSetup, setLoadingSetup] = useState(false);
	const [copiedField, setCopiedField] = useState('');
	const [setupError, setSetupError] = useState<string>('');

	const tabs = [
		{ id: 'details', name: 'Client Details', icon: Eye },
		{ id: 'setup', name: 'Setup Instructions', icon: Settings },
	];

	// Reset activeTab when modal opens
	useEffect(() => {
		if (isOpen) {
			setActiveTab('details');
			setSetupInstructions(null);
			setSetupError('');
		}
	}, [isOpen]);

	// Load setup instructions when setup tab is active
	useEffect(() => {
		if (isOpen && client && activeTab === 'setup' && !setupInstructions) {
			loadSetupInstructions();
		}
	}, [isOpen, client, activeTab]);

	// Functions for setup instructions
	const loadSetupInstructions = async (): Promise<void> => {
		if (!client?.id) {
			console.error('No client ID available');
			setSetupError('Client ID not available');
			return;
		}

		setLoadingSetup(true);
		setSetupError('');

		try {
			const response = await adminAPI.getSetupInstructions(client.id);

			if (response.success && response.data) {
				setSetupInstructions(response.data);
			} else {
				console.error('API returned unsuccessful response:', response);
				setSetupError('Failed to load setup instructions');
			}
		} catch (error: any) {
			console.error('Failed to load setup instructions:', error);
			setSetupError(error.message || 'Failed to load setup instructions');
		} finally {
			setLoadingSetup(false);
		}
	};

	const copyToClipboard = (text: string, field: string): void => {
		navigator.clipboard.writeText(text);
		setCopiedField(field);
		setTimeout(() => setCopiedField(''), 2000);
	};

	const handleZohoOAuth = (): void => {
		if (setupInstructions?.webhooks?.zoho?.oauthUrl) {
			window.open(setupInstructions.webhooks.zoho.oauthUrl, '_blank');
		}
	};

	if (!isOpen || !client) return null;

	// Ensure we have proper client data
	const clientData = (client as any).client || client;

	// Copy Button Component
	const CopyButton: React.FC<CopyButtonProps> = ({ text, field, label }) => (
		<div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
			<div className="flex-1">
				<div className="text-sm font-medium text-gray-700 mb-1">{label}</div>
				<code className="text-sm text-gray-900 break-all">{text}</code>
			</div>
			<button
				onClick={() => copyToClipboard(text, field)}
				className="flex items-center px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
				type="button">
				{copiedField === field ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
			</button>
		</div>
	);

	// Setup Instructions Content
	const renderSetupInstructions = (): React.ReactNode => {
		if (loadingSetup) {
			return (
				<div className="flex items-center justify-center h-64">
					<div className="flex items-center space-x-3">
						<RefreshCw className="animate-spin h-8 w-8 text-blue-600" />
						<span className="text-lg text-gray-600">Loading setup instructions...</span>
					</div>
				</div>
			);
		}

		if (setupError) {
			return (
				<div className="text-center py-8">
					<div className="text-red-600 mb-4">
						<p className="text-lg font-medium">Error loading setup instructions</p>
						<p className="text-sm">{setupError}</p>
					</div>
					<button
						onClick={loadSetupInstructions}
						className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
						type="button">
						Retry
					</button>
				</div>
			);
		}

		if (!setupInstructions) {
			return (
				<div className="text-center py-8">
					<p className="text-gray-500 mb-4">Click to load setup instructions</p>
					<button
						onClick={loadSetupInstructions}
						className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
						type="button">
						Load Setup Instructions
					</button>
				</div>
			);
		}

		return (
			<div className="space-y-6">
				{/* Header */}
				<div className="bg-blue-50 border-l-4 border-blue-400 p-4">
					<h3 className="text-lg font-medium text-blue-800">
						Integration Setup for {setupInstructions.clientInfo.name}
					</h3>
					<p className="text-sm text-blue-700 mt-1">
						⏱️ Estimated time: {setupInstructions.instructions.estimatedTime}
					</p>
				</div>

				{/* GaragePlug Webhooks */}
				<div className="border rounded-lg">
					<div className="px-4 py-3 bg-gray-50 border-b">
						<h4 className="font-semibold flex items-center">
							<span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-2">
								1
							</span>
							Configure GaragePlug Webhooks
						</h4>
					</div>
					<div className="p-4 space-y-4">
						<CopyButton text={setupInstructions.webhooks.garagePlug.url} field="gp-url" label="Webhook URL" />
						<CopyButton text={setupInstructions.webhooks.garagePlug.secret} field="gp-secret" label="Webhook Secret" />
						<div className="bg-gray-50 p-3 rounded text-sm">
							<strong>Location:</strong> {setupInstructions.webhooks.garagePlug.setupLocation}
						</div>
						<div className="bg-gray-50 p-3 rounded text-sm">
							<strong>Headers Required:</strong>
							<ul className="mt-2 space-y-1">
								{Object.entries(setupInstructions.webhooks.garagePlug.headers).map(([key, value]) => (
									<li key={key}>
										<code>
											{key}: {String(value)}
										</code>
									</li>
								))}
							</ul>
						</div>
						<div className="bg-gray-50 p-3 rounded text-sm">
							<strong>Events to Enable:</strong>
							<ul className="mt-2 grid grid-cols-2 gap-1">
								{setupInstructions.webhooks.garagePlug.events.map((event: string) => (
									<li key={event}>• {event}</li>
								))}
							</ul>
						</div>
					</div>
				</div>

				{/* Zoho OAuth - Only show for Zoho clients */}
				{(clientData.type === 'ZOHO_BOOKS' || client.integrations?.zoho?.enabled) && (
					<div className="border rounded-lg">
						<div className="px-4 py-3 bg-gray-50 border-b">
							<h4 className="font-semibold flex items-center">
								<span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-2">
									2
								</span>
								Zoho Books OAuth Authorization
							</h4>
						</div>
						<div className="p-4">
							<div className="flex items-center justify-between p-3 border rounded mb-4">
								<div className="flex items-center">
									<div
										className={`w-3 h-3 rounded-full mr-3 ${
											setupInstructions.webhooks.zoho.oauthStatus ? 'bg-green-500' : 'bg-red-500'
										}`}></div>
									<span>OAuth Status: {setupInstructions.webhooks.zoho.oauthStatus ? 'Completed' : 'Pending'}</span>
								</div>
								{!setupInstructions.webhooks.zoho.oauthStatus && (
									<button
										onClick={handleZohoOAuth}
										className="flex items-center px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
										type="button">
										<ExternalLink className="h-4 w-4 mr-2" />
										Start OAuth
									</button>
								)}
							</div>
							{!setupInstructions.webhooks.zoho.oauthStatus && (
								<div className="bg-blue-50 p-3 rounded text-sm text-blue-700">
									Complete OAuth authorization before configuring webhooks in Step 3.
								</div>
							)}
						</div>
					</div>
				)}

				{/* Zoho Webhooks - Only show for Zoho clients */}
				{(clientData.type === 'ZOHO_BOOKS' || client.integrations?.zoho?.enabled) && (
					<div className="border rounded-lg">
						<div className="px-4 py-3 bg-gray-50 border-b">
							<h4 className="font-semibold flex items-center">
								<span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-2">
									3
								</span>
								Configure Zoho Books Webhooks
							</h4>
						</div>
						<div className="p-4 space-y-4">
							<CopyButton text={setupInstructions.webhooks.zoho.url} field="zoho-url" label="Webhook URL" />
							<CopyButton text={setupInstructions.webhooks.zoho.secret} field="zoho-secret" label="Webhook Secret" />
							<div className="bg-gray-50 p-3 rounded text-sm">
								<strong>Location:</strong> {setupInstructions.webhooks.zoho.setupLocation}
							</div>
							<div className="bg-gray-50 p-3 rounded text-sm">
								<strong>Headers (Auto-added by Zoho):</strong>
								<ul className="mt-2 space-y-1">
									{Object.entries(setupInstructions.webhooks.zoho.headers).map(([key, value]) => (
										<li key={key}>
											<code>
												{key}: {String(value)}
											</code>
										</li>
									))}
								</ul>
							</div>
							{setupInstructions.webhooks.zoho.customHeaders && (
								<div className="bg-yellow-50 border border-yellow-200 p-3 rounded text-sm">
									<strong className="text-yellow-800">Manual Header Configuration Required:</strong>
									<p className="mt-2 text-yellow-700">{setupInstructions.webhooks.zoho.customHeaders.note}</p>
									<div className="mt-3 space-y-2">
										<div>
											<strong>Header Name:</strong>{' '}
											<code className="bg-white px-2 py-1 rounded">
												{setupInstructions.webhooks.zoho.customHeaders.headerName}
											</code>
										</div>
										<div>
											<strong>Possible Values:</strong>
											<ul className="mt-1 ml-4">
												{setupInstructions.webhooks.zoho.customHeaders.possibleValues.map((value) => (
													<li key={value} className="list-disc">
														<code className="bg-white px-2 py-1 rounded">{value}</code>
													</li>
												))}
											</ul>
										</div>
										<div>
											<strong>Description:</strong>{' '}
											<span className="text-yellow-700">
												{setupInstructions.webhooks.zoho.customHeaders.description}
											</span>
										</div>
									</div>
								</div>
							)}
							<div className="bg-gray-50 p-3 rounded text-sm">
								<strong>Events to Enable:</strong>
								<ul className="mt-2 grid grid-cols-2 gap-1">
									{setupInstructions.webhooks.zoho.events.map((event: string) => (
										<li key={event}>• {event}</li>
									))}
								</ul>
							</div>
						</div>
					</div>
				)}

				{/* Country URLs - Only show for Zoho clients */}
				{(clientData.type === 'ZOHO_BOOKS' || client.integrations?.zoho?.enabled) && (
					<div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
						<h4 className="font-medium text-yellow-800 mb-2">Zoho Country-Specific URLs</h4>
						<p className="text-sm text-yellow-700 mb-2">{setupInstructions.countryUrls.description}</p>
						<a
							href={setupInstructions.countryUrls.reference}
							target="_blank"
							rel="noopener noreferrer"
							className="text-yellow-600 hover:text-yellow-800 text-sm font-medium">
							View Official Country-Specific URLs →
						</a>
						<div className="mt-3 grid grid-cols-2 gap-2 text-xs">
							{Object.entries(setupInstructions.countryUrls.examples).map(([country, urls]) => (
								<div key={country} className="bg-white p-2 rounded">
									<strong>{country}:</strong> <code className="text-xs">{urls.baseUrl}</code>
								</div>
							))}
						</div>
					</div>
				)}
			</div>
		);
	};

	return createPortal(
		<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[55] p-4">
			<div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
				{/* Header */}
				<div className="flex items-center justify-between p-6 border-b">
					<h2 className="text-xl font-semibold text-gray-900">Client Details: {clientData.name}</h2>
					<button onClick={onClose} className="text-gray-400 hover:text-gray-600" type="button">
						<X className="h-6 w-6" />
					</button>
				</div>

				{/* Tabs Navigation */}
				<div className="border-b border-gray-200">
					<nav className="flex space-x-8 px-6">
						{tabs.map((tab) => (
							<button
								key={tab.id}
								onClick={() => {
									setActiveTab(tab.id);
								}}
								className={`${
									activeTab === tab.id
										? 'border-blue-500 text-blue-600'
										: 'border-transparent text-gray-500 hover:text-gray-700'
								} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center transition-colors`}
								type="button">
								<tab.icon className="h-4 w-4 mr-2" />
								{tab.name}
							</button>
						))}
					</nav>
				</div>

				{/* Tab Content */}
				<div className="flex-1 overflow-y-auto p-6">
					{activeTab === 'details' && (
						<div className="space-y-8">
							{/* Basic Information */}
							<div>
								<h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
								<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">Client Name</label>
										<p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">{clientData.name}</p>
									</div>
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
										<p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">{clientData.email}</p>
									</div>
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
										<div className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
											<span
												className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
													clientData.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
												}`}>
												{clientData.status}
											</span>
										</div>
									</div>
								</div>
							</div>

							{/* Integration Status */}
							<div>
								<h3 className="text-lg font-medium text-gray-900 mb-4">Integration Status</h3>
								<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
									<div className="border rounded-lg p-4">
										<h4 className="font-medium text-gray-900 mb-3">Zoho Books</h4>
										<div className="space-y-2">
											<div className="flex justify-between">
												<span className="text-sm text-gray-600">Enabled:</span>
												<span
													className={`text-sm ${clientData.integrations?.zoho?.enabled || client.integrations?.zoho?.enabled ? 'text-green-600' : 'text-gray-500'}`}>
													{clientData.integrations?.zoho?.enabled || client.integrations?.zoho?.enabled ? 'Yes' : 'No'}
												</span>
											</div>
											<div className="flex justify-between">
												<span className="text-sm text-gray-600">Configured:</span>
												<span
													className={`text-sm ${clientData.integrations?.zoho?.configured || client.integrations?.zoho?.configured ? 'text-green-600' : 'text-yellow-600'}`}>
													{clientData.integrations?.zoho?.configured || client.integrations?.zoho?.configured
														? 'Yes'
														: 'Setup Required'}
												</span>
											</div>
										</div>
									</div>
									<div className="border rounded-lg p-4">
										<h4 className="font-medium text-gray-900 mb-3">Odoo Finance</h4>
										<div className="space-y-2">
											<div className="flex justify-between">
												<span className="text-sm text-gray-600">Enabled:</span>
												<span
													className={`text-sm ${clientData.integrations?.odoo?.enabled || client.integrations?.odoo?.enabled ? 'text-green-600' : 'text-gray-500'}`}>
													{clientData.integrations?.odoo?.enabled || client.integrations?.odoo?.enabled ? 'Yes' : 'No'}
												</span>
											</div>
											<div className="flex justify-between">
												<span className="text-sm text-gray-600">Configured:</span>
												<span
													className={`text-sm ${clientData.integrations?.odoo?.configured || client.integrations?.odoo?.configured ? 'text-green-600' : 'text-yellow-600'}`}>
													{clientData.integrations?.odoo?.configured || client.integrations?.odoo?.configured
														? 'Yes'
														: 'Setup Required'}
												</span>
											</div>
										</div>
									</div>
									<div className="border rounded-lg p-4">
										<h4 className="font-medium text-gray-900 mb-3">Microsoft Dynamics 365</h4>
										<div className="space-y-2">
											<div className="flex justify-between">
												<span className="text-sm text-gray-600">Enabled:</span>
												<span
													className={`text-sm ${clientData.integrations?.msd?.enabled || client.integrations?.msd?.enabled ? 'text-green-600' : 'text-gray-500'}`}>
													{clientData.integrations?.msd?.enabled || client.integrations?.msd?.enabled ? 'Yes' : 'No'}
												</span>
											</div>
											<div className="flex justify-between">
												<span className="text-sm text-gray-600">Configured:</span>
												<span
													className={`text-sm ${clientData.integrations?.msd?.configured || client.integrations?.msd?.configured ? 'text-green-600' : 'text-yellow-600'}`}>
													{clientData.integrations?.msd?.configured || client.integrations?.msd?.configured
														? 'Yes'
														: 'Setup Required'}
												</span>
											</div>
										</div>
									</div>
								</div>
							</div>

							{/* Locations */}
							<div>
								<h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
									<MapPin className="h-5 w-5 mr-2 text-blue-600" />
									Locations
								</h3>
								{clientData.locations && clientData.locations.length > 0 ? (
									<div className="space-y-3">
										{clientData.locations.map((location: any, index: number) => (
											<div
												key={location.id || index}
												className={`border rounded-lg p-4 ${location.isHeadOffice ? 'bg-amber-50 border-amber-300' : 'bg-gray-50'}`}>
												<div className="flex items-center gap-2 mb-3">
													<span className="text-sm font-medium text-gray-900">{location.name}</span>
													{location.isHeadOffice && (
														<span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300">
															Head Office
														</span>
													)}
												</div>
												<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
													<div>
														<label className="block text-xs font-medium text-gray-600 mb-1">GP Location ID</label>
														<p className="text-sm text-gray-900 font-mono bg-white px-2 py-1 rounded border">
															{location.gpLocationId}
														</p>
													</div>
													<div>
														<label className="block text-xs font-medium text-gray-600 mb-1">Target Location ID</label>
														<p className="text-sm text-gray-900 font-mono bg-white px-2 py-1 rounded border">
															{location.targetLocationId || 'Not set'}
														</p>
													</div>
												</div>
												<div className="mt-3 flex items-center justify-between">
													<div className="flex items-center space-x-4">
														<div>
															<label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
															<span
																className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
																	location.status === 'ACTIVE'
																		? 'bg-green-100 text-green-800'
																		: 'bg-gray-100 text-gray-800'
																}`}>
																{location.status || 'ACTIVE'}
															</span>
														</div>
													</div>
													{(location.createdAt || location.updatedAt) && (
														<div className="flex items-center space-x-6 text-xs text-gray-500">
															{location.createdAt && (
																<div>
																	<span className="font-medium text-gray-600">Created:</span>{' '}
																	{new Date(location.createdAt).toLocaleDateString()}
																</div>
															)}
															{location.updatedAt && (
																<div>
																	<span className="font-medium text-gray-600">Updated:</span>{' '}
																	{new Date(location.updatedAt).toLocaleDateString()}
																</div>
															)}
														</div>
													)}
												</div>
											</div>
										))}
									</div>
								) : (
									<div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
										<MapPin className="mx-auto h-12 w-12 text-gray-400" />
										<h3 className="mt-2 text-sm font-medium text-gray-900">No locations</h3>
										<p className="mt-1 text-sm text-gray-500">This client doesn't have any locations configured.</p>
									</div>
								)}
							</div>

							{/* Health & Performance */}
							<div>
								<h3 className="text-lg font-medium text-gray-900 mb-4">Health & Performance</h3>
								<div className="grid grid-cols-1 md:grid-cols-4 gap-6">
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">Health Status</label>
										<div className="flex items-center space-x-2">
											{(clientData.health?.status || client.health?.status) === 'healthy' && (
												<CheckCircle className="h-4 w-4 text-green-500" />
											)}
											{(clientData.health?.status || client.health?.status) === 'warning' && (
												<AlertTriangle className="h-4 w-4 text-yellow-500" />
											)}
											{(clientData.health?.status || client.health?.status) === 'error' && (
												<XCircle className="h-4 w-4 text-red-500" />
											)}
											<span className="text-sm capitalize text-gray-900">
												{clientData.health?.status || client.health?.status || 'Unknown'}
											</span>
										</div>
									</div>
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">Last 7 Days</label>
										<p className="text-sm text-gray-900">
											{(clientData.monthlyVolume || client.monthlyVolume || 0).toLocaleString()} syncs
										</p>
									</div>
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">Success Rate</label>
										<p className="text-sm text-green-600 font-medium">
											{(clientData.syncStats?.successRate || client.syncStats?.successRate || 0).toFixed(2)}%
										</p>
									</div>
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">Failed Syncs</label>
										<p className="text-sm text-red-600 font-medium">
											{clientData.syncStats?.failedSyncs || client.syncStats?.failedSyncs || 0} failed
										</p>
									</div>
								</div>
							</div>

							{/* Configuration - Show All Real Database Fields */}
							<div>
								<h3 className="text-lg font-medium text-gray-900 mb-4">Configuration</h3>
								<div className="space-y-4">
									{/* Database Core Fields */}
									<div className="border rounded-lg p-4">
										<h4 className="font-medium text-gray-900 mb-3">Database Core Fields</h4>
										<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
											<div>
												<label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
												<p className="text-sm text-gray-900 bg-gray-50 p-2 rounded font-mono">
													{clientData.type || 'Not configured'}
												</p>
											</div>
										</div>
									</div>

									{/* GaragePlug Integration */}
									<div className="border rounded-lg p-4">
										<h4 className="font-medium text-gray-900 mb-3">GaragePlug Integration</h4>
										<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
											<div>
												<label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
												<p className="text-sm text-gray-900 bg-gray-50 p-2 rounded font-mono break-all">
													{clientData.apiKey || 'Not configured'}
												</p>
											</div>
											<div>
												<label className="block text-sm font-medium text-gray-700 mb-1">Client ID</label>
												<p className="text-sm text-gray-900 bg-gray-50 p-2 rounded font-mono break-all">
													{clientData.gpClientId || 'Not configured'}
												</p>
											</div>
											<div>
												<label className="block text-sm font-medium text-gray-700 mb-1">Client Secret</label>
												<p className="text-sm text-gray-900 bg-gray-50 p-2 rounded font-mono break-all">
													{clientData.gpClientSecret || 'Not configured'}
												</p>
											</div>
											<div>
												<label className="block text-sm font-medium text-gray-700 mb-1">Webhook Secret</label>
												<p className="text-sm text-gray-900 bg-gray-50 p-2 rounded font-mono break-all">
													{clientData.gpWebhookSecret || 'Not configured'}
												</p>
											</div>
										</div>
									</div>

									{/* Zoho Books Configuration from Config JSON */}
									{(clientData.type === 'ZOHO_BOOKS' || client.integrations?.zoho?.enabled) && (
										<div className="border rounded-lg p-4">
											<h4 className="font-medium text-gray-900 mb-3">Zoho Books Configuration (from Config JSON)</h4>
											<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
												<div>
													<label className="block text-sm font-medium text-gray-700 mb-1">Region</label>
													<p className="text-sm text-gray-900 bg-gray-50 p-2 rounded font-mono">
														{clientData.config?.zohoRegion || 'COM'}
													</p>
												</div>
												<div>
													<label className="block text-sm font-medium text-gray-700 mb-1">Client ID</label>
													<p className="text-sm text-gray-900 bg-gray-50 p-2 rounded font-mono break-all">
														{clientData.config?.zohoClientId || 'Not configured'}
													</p>
												</div>
												<div>
													<label className="block text-sm font-medium text-gray-700 mb-1">Client Secret</label>
													<p className="text-sm text-gray-900 bg-gray-50 p-2 rounded font-mono break-all">
														{clientData.config?.zohoClientSecret || 'Not configured'}
													</p>
												</div>
												<div>
													<label className="block text-sm font-medium text-gray-700 mb-1">Scopes</label>
													<p className="text-sm text-gray-900 bg-gray-50 p-2 rounded font-mono break-all">
														{clientData.config?.zohoScopes || 'Not configured'}
													</p>
												</div>
												<div>
													<label className="block text-sm font-medium text-gray-700 mb-1">Organization ID</label>
													<p className="text-sm text-gray-900 bg-gray-50 p-2 rounded font-mono">
														{clientData.config?.zohoOrganizationId || 'Not configured'}
													</p>
												</div>
												<div>
													<label className="block text-sm font-medium text-gray-700 mb-1">
														Webhook Secret (from Config)
													</label>
													<p className="text-sm text-gray-900 bg-gray-50 p-2 rounded font-mono break-all">
														{clientData.config?.zohoWebhookSecret || 'Not configured'}
													</p>
												</div>
											</div>
										</div>
									)}

									{/* MSD Finance Configuration from Config JSON */}
									{(clientData.type === 'MSD_FINANCE' || client.integrations?.msd?.enabled) && (
										<div className="border rounded-lg p-4">
											<h4 className="font-medium text-gray-900 mb-3">Microsoft Dynamics 365 Finance Configuration</h4>
											<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
												<div>
													<label className="block text-sm font-medium text-gray-700 mb-1">Resource URL</label>
													<p className="text-sm text-gray-900 bg-gray-50 p-2 rounded font-mono break-all">
														{clientData.config?.msdResource || 'Not configured'}
													</p>
												</div>
												<div>
													<label className="block text-sm font-medium text-gray-700 mb-1">Client ID</label>
													<p className="text-sm text-gray-900 bg-gray-50 p-2 rounded font-mono break-all">
														{clientData.config?.msdClientId || 'Not configured'}
													</p>
												</div>
												<div>
													<label className="block text-sm font-medium text-gray-700 mb-1">Client Secret</label>
													<p className="text-sm text-gray-900 bg-gray-50 p-2 rounded font-mono break-all">
														{clientData.config?.msdClientSecret || 'Not configured'}
													</p>
												</div>
												<div>
													<label className="block text-sm font-medium text-gray-700 mb-1">Tenant ID</label>
													<p className="text-sm text-gray-900 bg-gray-50 p-2 rounded font-mono break-all">
														{clientData.config?.msdTenantId || 'Not configured'}
													</p>
												</div>
												<div>
													<label className="block text-sm font-medium text-gray-700 mb-1">
														Legal Entity (Data Area ID)
													</label>
													<p className="text-sm text-gray-900 bg-gray-50 p-2 rounded font-mono break-all">
														{clientData.config?.msdDataAreaId || 'Not configured'}
													</p>
												</div>
											</div>

											{/* Service Bus Configuration */}
											{(clientData.config?.msdServiceBusConnectionString ||
												clientData.config?.msdServiceBusQueueName) && (
												<div className="mt-4 pt-4 border-t">
													<h5 className="font-medium text-gray-900 mb-3 text-sm">Service Bus Configuration</h5>
													<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
														<div>
															<label className="block text-sm font-medium text-gray-700 mb-1">Connection String</label>
															<p className="text-sm text-gray-900 bg-gray-50 p-2 rounded font-mono break-all">
																{clientData.config?.msdServiceBusConnectionString || 'Not configured'}
															</p>
														</div>
														<div>
															<label className="block text-sm font-medium text-gray-700 mb-1">
																Service Bus Queue Name
															</label>
															<p className="text-sm text-gray-900 bg-gray-50 p-2 rounded font-mono break-all">
																{clientData.config?.msdServiceBusQueueName || 'Not configured'}
															</p>
														</div>
													</div>
												</div>
											)}
										</div>
									)}

									{/* Odoo Finance Configuration from Config JSON */}
									{(clientData.type === 'ODOO_FINANCE' || client.integrations?.odoo?.enabled) && (
										<div className="border rounded-lg p-4">
											<h4 className="font-medium text-gray-900 mb-3">Odoo Finance Configuration</h4>
											<div className="space-y-4">
												{/* Row 1: Base URL | Database */}
												<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
													<div>
														<label className="block text-sm font-medium text-gray-700 mb-1">Base URL</label>
														<p className="text-sm text-gray-900 bg-gray-50 p-2 rounded font-mono break-all">
															{clientData.config?.odooBaseUrl || 'Not configured'}
														</p>
													</div>
													<div>
														<label className="block text-sm font-medium text-gray-700 mb-1">Database</label>
														<p className="text-sm text-gray-900 bg-gray-50 p-2 rounded font-mono break-all">
															{clientData.config?.odooDatabase || 'Not configured'}
														</p>
													</div>
												</div>

												{/* Row 2: Username | API Key */}
												<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
													<div>
														<label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
														<p className="text-sm text-gray-900 bg-gray-50 p-2 rounded font-mono break-all">
															{clientData.config?.odooUsername || 'Not configured'}
														</p>
													</div>
													<div>
														<label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
														<p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
															{clientData.config?.odooApiKey || 'Not configured'}
														</p>
													</div>
												</div>

												{/* Row 3: Automation IDs */}
												<div className="col-span-full">
													<label className="block text-sm font-medium text-gray-700 mb-1">Automation IDs</label>
													{(clientData.config?.odooAutomationIds as string[] | undefined)?.length ? (
														<div className="flex flex-wrap gap-2">
															{(clientData.config?.odooAutomationIds as string[]).map((id: string) => (
																<span
																	key={id}
																	className="px-2 py-1 bg-gray-100 border border-gray-200 rounded font-mono text-sm text-gray-700">
																	#{id}
																</span>
															))}
														</div>
													) : (
														<p className="text-sm text-amber-600 bg-amber-50 px-2 py-1 rounded">
															Not configured — Odoo webhook routing will fail
														</p>
													)}
												</div>
											</div>
										</div>
									)}
								</div>
							</div>

							{/* Timestamps */}
							<div>
								<h3 className="text-lg font-medium text-gray-900 mb-4">Timestamps</h3>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">Created At</label>
										<p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
											{new Date(clientData.createdAt || client.createdAt).toLocaleString()}
										</p>
									</div>
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">Last Updated</label>
										<p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
											{new Date(clientData.updatedAt || client.updatedAt).toLocaleString()}
										</p>
									</div>
								</div>
							</div>
						</div>
					)}
					{activeTab === 'setup' && renderSetupInstructions()}
				</div>

				{/* Footer */}
				<div className="flex justify-end p-6 border-t">
					<button
						onClick={onClose}
						className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
						type="button">
						Close
					</button>
				</div>
			</div>
		</div>,
		document.body,
	);
};
